import React, { useEffect, useState, useRef } from "react";
import api from "../../api/api";
import "../../css/Employee.css";

const Attendance = () => {
  // Functions moved here, before useState calls
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getMonthStartDate = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split("T")[0];
  };

  const getMonthEndDate = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];
  };

  const getEffectiveStartDate = () => {
    return startDate || getMonthStartDate();
  };

  const getEffectiveEndDate = () => {
    return endDate || getMonthEndDate();
  };

  // Now useState hooks can safely call getTodayDate()
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showDatePickers, setShowDatePickers] = useState(false);
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [employeesPerPage, setEmployeesPerPage] = useState(10);
  const [showListFilter, setShowListFilter] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [companies, setCompanies] = useState([]);

  const [states, setStates] = useState([]); // ✅ added
  const [selectedState, setSelectedState] = useState(""); // ✅ added

  const [selectedDate, setSelectedDate] = useState(getTodayDate());

  const [zoomImageUrl, setZoomImageUrl] = useState(null);

  const dateInputRef = useRef(null);
  const listFilterRef = useRef(null);

  useEffect(() => {
    const todayDate = getTodayDate();

    api
      .get("/employee/attendance")
      .then((response) => {
        if (response.data.success) {
          setAttendanceData(response.data.data);
          const todayData = response.data.data.filter(
            (emp) => emp.date === todayDate
          );
          setFilteredData(todayData);
        }
      })
      .catch((error) => {
        console.error("Error fetching attendance:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    api
      .get("/get/company")
      .then((response) => {
        if (response.data.success) {
          setCompanies(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching companies:", error);
      });
  }, []);

  useEffect(() => {
    api
      .get("/get/state")
      .then((response) => {
        if (response.data.success) {
          setStates(response.data.data);
        }
      })
      .catch((error) => {
        console.error("Error fetching states:", error);
      });
  }, []);

  // Filtering attendance data on every filter change
  useEffect(() => {
    // If all filters are empty, show only today's data
    if (
      !searchTerm &&
      !statusFilter &&
      !selectedCompany &&
      !selectedState &&
      !startDate &&
      !endDate
    ) {
      const todayDate = getTodayDate();
      const todayData = attendanceData.filter((emp) => emp.date === todayDate);
      setFilteredData(todayData);
      setCurrentPage(1);
      return;
    }

    // Otherwise, apply all filters
    const effStart = startDate || getMonthStartDate();
    const effEnd = endDate || getMonthEndDate();

    const filtered = attendanceData.filter((emp) => {
      const fullName = `${emp.firstname || ""} ${
        emp.lastname || ""
      }`.toLowerCase();
      const email = (emp.email || "").toLowerCase();
      const company = (emp.company_name || "").toLowerCase();
      const department = (emp.department_name || "").toLowerCase();
      const designation = (emp.designation || "").toLowerCase();
      const date = (emp.date || "").toLowerCase();
      const clockIn = (emp.clock_in || "").toLowerCase();
      const clockOut = (emp.clock_out || "").toLowerCase();
      const startReading = (emp.meter_reading || "").toString().toLowerCase();
      const endReading = (emp.clock_out_meter_reading || "")
        .toString()
        .toLowerCase();
      const startBreak = (emp.break_start_time || "").toLowerCase();
      const endBreak = (emp.break_end_time || "").toLowerCase();
      const status = (emp.status || "").toLowerCase();
      const state = (emp.state || "").toLowerCase();
      const phone = (emp.phone || "").toLowerCase();

      const search = searchTerm.toLowerCase();

      const matchesSearch =
        fullName.includes(search) ||
        email.includes(search) ||
        company.includes(search) ||
        department.includes(search) ||
        designation.includes(search) ||
        date.includes(search) ||
        clockIn.includes(search) ||
        clockOut.includes(search) ||
        startReading.includes(search) ||
        endReading.includes(search) ||
        startBreak.includes(search) ||
        endBreak.includes(search) ||
        state.includes(search) ||
        phone.includes(search) ||
        status.includes(search);

      const matchesStatus =
        !statusFilter || status === statusFilter.toLowerCase();
      const matchesCompany =
        !selectedCompany || company === selectedCompany.toLowerCase();
      const matchesState =
        !selectedState || state === selectedState.toLowerCase();

      const empDate = emp.date || "";

      const matchesDateRange = empDate >= effStart && empDate <= effEnd;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesCompany &&
        matchesState &&
        matchesDateRange
      );
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [
    searchTerm,
    statusFilter,
    selectedCompany,
    selectedState,
    attendanceData,
    startDate,
    endDate,
  ]);

  const indexOfLastEmployee = currentPage * employeesPerPage;
  const indexOfFirstEmployee = indexOfLastEmployee - employeesPerPage;
  const currentEmployees = filteredData.slice(
    indexOfFirstEmployee,
    indexOfLastEmployee
  );
  const totalPages = Math.ceil(filteredData.length / employeesPerPage);

  const handlePerPageChange = (value) => {
    setEmployeesPerPage(value);
    setShowListFilter(false);
    setCurrentPage(1);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        listFilterRef.current &&
        !listFilterRef.current.contains(event.target)
      ) {
        setShowListFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const closeZoomModal = () => {
    setZoomImageUrl(null);
  };

  return (
    <section className="employee-section">
      <div className="inner-content">
        <div className="filter-wrapper">
          <h2>Employee Attendance</h2>
          <div className="right-filter">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="select-status tooltip-wrapper" data-tooltip="Filter by Status">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">All Status</option>
                <option value="Present">Present</option>
                <option value="Absent">Absent</option>
                <option value="Late">Late</option>
              </select>
            </div>

            <div className="select-company tooltip-wrapper" data-tooltip="Filter by Company">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
              >
                <option value="">All Companies</option>
                {companies.map((company) => (
                  <option key={company.id} value={company.name}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="state-select tooltip-wrapper" data-tooltip="Filter by State">
              <select
                value={selectedState}
                onChange={(e) => setSelectedState(e.target.value)}
              >
                <option value="">All States</option>
                {states.map((state) => (
                  <option key={state.state_id} value={state.state_title}>
                    {state.state_title}
                  </option>
                ))}
              </select>
            </div>

            <div className="date-filter tooltip-wrapper" style={{ position: "relative" }} data-tooltip="Filter by Date">
              <button onClick={() => setShowDatePickers((prev) => !prev)}>
                <i className="fa-solid fa-calendar"></i>
              </button>
              {showDatePickers && (
                <div
                  className="date-popup"
                  style={{
                    position: "fixed",
                    top: "125px",
                    right: "15px",
                    background: "#fff",
                    padding: "12px 15px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 100,
                    display: "flex",
                    gap: "10px",
                  }}
                >
                  <input
                    ref={startDateInputRef}
                    type="date"
                    value={startDate}
                    max={getTodayDate()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setStartDate(val);
                      if (val && val > endDate) setEndDate(val);
                      if (val && endDate) setShowDatePickers(false);
                    }}
                  />
                  <input
                    ref={endDateInputRef}
                    type="date"
                    value={endDate}
                    min={startDate}
                    max={getTodayDate()}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEndDate(val);
                      if (val && val < startDate) setStartDate(val);
                      if (startDate && val) setShowDatePickers(false);
                    }}
                  />
                </div>
              )}
            </div>

            <div className="list-fliter tooltip-wrapper" ref={listFilterRef} data-tooltip="Filter by List">
              <button onClick={() => setShowListFilter(!showListFilter)}>
                <i className="fa-solid fa-filter"></i>
              </button>
              {showListFilter && (
                <div className="filter-data">
                  {[10, 30, 50, 100].map((number) => (
                    <div
                      key={number}
                      className={`filter-option ${
                        employeesPerPage === number ? "selected" : ""
                      }`}
                      onClick={() => handlePerPageChange(number)}
                    >
                      {number}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="employee-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Full Name</th>
                    <th>Phone</th>
                    <th>Company</th>
                    <th>Department</th>
                    <th>Designation</th>
                    <th>Date</th>
                    <th>State</th>
                    <th>Clock In</th>
                    <th>Clock Out</th>
                    <th>Start Reading</th>
                    <th>End Reading</th>
                    <th>Start Break</th>
                    <th>End Break</th>
                    <th>Device</th>
                    <th>Start Meter Image</th>
                    <th>End Meter Image</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {currentEmployees.map((emp, index) => (
                    <tr key={index}>
                      <td>{indexOfFirstEmployee + index + 1}</td>
                      <td>
                        {emp.firstname} {emp.lastname}
                      </td>
                      <td className="phone">
                        <i class="fa-solid fa-phone"></i>&nbsp; {emp.phone}
                      </td>
                      <td className="p-day">{emp.company_name}</td>
                      <td>{emp.department_name}</td>
                      <td>{emp.designation}</td>
                      <td className="check-in">{emp.date}</td>
                      <td className="state">{emp.state}</td>
                      <td className="check-in">{emp.clock_in}</td>
                      <td className="check-out">{emp.clock_out}</td>
                      <td className="check-in">{emp.meter_reading}</td>
                      <td className="check-out">{emp.clock_out_meter_reading}</td>
                      <td className="check-in">{emp.break_start_time}</td>
                      <td className="check-out">{emp.break_end_time}</td>
                      <td>{emp.agent_type}</td>
                      <td>
                        {emp.meter_image ? (
                          <i
                            className="fa-solid fa-images"
                            style={{
                              fontSize: "35px",
                              color: "#5b8c51",
                              cursor: "pointer",
                            }}
                            onClick={() => setZoomImageUrl(emp.meter_image)}
                            title="View Start Meter Image"
                          ></i>
                        ) : (
                          ""
                        )}
                      </td>

                      <td>
                        {emp.clock_out_meter_image ? (
                          <i
                            className="fa-solid fa-images"
                            style={{
                              fontSize: "35px",
                              color: "#5b8c51",
                              cursor: "pointer",
                            }}
                            onClick={() =>
                              setZoomImageUrl(emp.clock_out_meter_image)
                            }
                            title="View End Meter Image"
                          ></i>
                        ) : (
                          ""
                        )}
                      </td>

                      <td
                        style={{
                          color:
                            emp.status === "Present"
                              ? "#28a745"
                              : emp.status === "Absent"
                              ? "#f64b34"
                              : emp.status === "Late"
                              ? "#ff9d17"
                              : "inherit",
                          fontWeight: "bold",
                        }}
                      >
                        <span className="status">{emp.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Prev
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </>
        )}

        {zoomImageUrl && (
          <div className="zoom-modal-overlay" onClick={closeZoomModal}>
            <div
              className="zoom-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="close-btn" onClick={closeZoomModal}>
                <i className="fa-solid fa-xmark"></i>
              </button>
              <img src={zoomImageUrl} alt="Zoomed" className="zoomed-image" />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Attendance;
